import React from 'react';
import { Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import { parse } from 'date-fns';
import { groupItemsByCreatedAt } from '../utils/GroupingUtils';
import { Info } from 'lucide-react';

interface Thread {
  id: number;
  name?: string;
  created_at?: string;
  updated_at?: string;
}

interface ThreadListProps {
  conversations: Thread[];
  isLoading: boolean;
  currentThreadId?: number;
  onThreadSelect: (threadId: number) => void;
  onThreadDelete: (e: React.MouseEvent, threadId: number) => void;
}

const ThreadList: React.FC<ThreadListProps> = ({
  conversations,
  isLoading,
  currentThreadId,
  onThreadSelect,
  onThreadDelete,
}) => {
  if (isLoading) {
    return (
      <ThreadListContainer>
        <LoadingMessage>Loading threads...</LoadingMessage>
      </ThreadListContainer>
    );
  }

  if (!conversations || !Array.isArray(conversations) || conversations.length === 0) {
    return (
      <ThreadListContainer>
        <EmptyMessage>No threads yet. Start a conversation!</EmptyMessage>
      </ThreadListContainer>
    );
  }

  const groupedConversations = groupItemsByCreatedAt<Thread>(conversations);

  const sortedGroups = Object.entries(groupedConversations)
    .filter(([, items]) => items.length !== 0)
    .sort(([labelA], [labelB]) => {
      const fixedOrder: Record<string, number> = {
        today: 0,
        'last 7 days': 1,
        'last month': 2,
      };

      const isFixedA = labelA in fixedOrder;
      const isFixedB = labelB in fixedOrder;

      if (isFixedA && isFixedB) {
        return fixedOrder[labelA] - fixedOrder[labelB];
      }
      if (isFixedA) {
        return -1;
      }
      if (isFixedB) {
        return 1;
      }

      const dateA = parse(labelA, 'MMMM yyyy', new Date()).getTime();
      const dateB = parse(labelB, 'MMMM yyyy', new Date()).getTime();
      return dateB - dateA;
    });

  return (
    <ThreadListContainer>
<InfoPanelHeader>
  Recent Threads
</InfoPanelHeader>
      
      {sortedGroups.map(([sectionTitle, items], index) => (
        <GroupSection key={`${sectionTitle}-${index}`}>
          <SectionLabel>{sectionTitle}</SectionLabel>
          {items.map((thread: Thread) => (
            <ThreadItem
              key={thread.id}
              $isSelected={thread.id === currentThreadId}
              onClick={() => onThreadSelect(thread.id)}
            >
              <ThreadName>{thread.name || `Thread ${thread.id}`}</ThreadName>
              <ThreadActions onClick={e => e.stopPropagation()}>
                <DeleteButton
                  onClick={e => {
                    e.stopPropagation();
                    onThreadDelete(e, thread.id);
                  }}
                  title="Delete thread"
                >
                  <Icon name="trash" />
                </DeleteButton>
              </ThreadActions>
            </ThreadItem>
          ))}
        </GroupSection>
      ))}
    </ThreadListContainer>
  );
};

export default ThreadList;

const ThreadListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const GroupSection = styled.div`
  margin-bottom: 12px;
`;

const InfoPanelHeader = styled.div`
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 14px;
  color: #333333;
  font-family: 'Poppins', sans-serif;
`;

const SectionLabel = styled.div`
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  color: #333333;
  text-transform: capitalize;
  letter-spacing: 0.02em;
`;

const DeleteButton = styled.button`
  appearance: none;
  background: none;
  border: none;
  outline: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  min-width: 24px;
  min-height: 24px;
  padding: 0;
  color: #999;
  opacity: 0;
  visibility: hidden;
  line-height: 1;
  transform: translateY(1px);
  transition: opacity 150ms ease, background 150ms ease, transform 150ms ease;
  border-radius: 25%;

  svg,
  i {
    display: block;
  }

  &:hover {
    color: #2d70e2;
    background: rgba(45, 112, 226, 0.12);
  }

  &:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

const ThreadItem = styled.div<{ $isSelected: boolean }>`
  display: flex;
  font-size: 14px;
  font-weight: 400;
  font-family: 'Poppins', sans-serif;
  justify-content: space-between;
  align-items: center;
  padding: 2px 8px 2px 12px;
  border-radius: 12px;
  cursor: pointer;
  background-color: ${props => (props.$isSelected ? '#F8FAFF' : '#FFFFFF')};
  border: none;
  transition: background-color 150ms ease, box-shadow 150ms ease;

  &:hover {
    background-color: #f8faff;
    box-shadow: inset 0 0 0 1px rgba(45, 112, 226, 0.18);
  }

  &:hover ${DeleteButton} {
    opacity: 1;
    visibility: visible;
  }
`;

const ThreadActions = styled.div`
  display: flex;
  align-items: center;
`;

const ThreadName = styled.div`
  font-size: 13px;
  color: #777777;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  padding-right: 8px;
`;

const LoadingMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666d79;
  font-size: 14px;
`;

const EmptyMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666d79;
  font-size: 14px;
  text-align: center;
  padding: 32px;
`;


